import { useState, useEffect } from 'react';
import { makeGetCall } from '../utils/helper';

/**
 * Like useModalities, but returns only the modalities the LOGGED-IN user is
 * allowed to see (T001 c/d). Use this for the modality filter on list pages
 * that must respect per-user modality access (PACS / Orders / My Worklist).
 *
 * The backend (/my-modalities) returns all active modalities when the user has
 * no restriction configured or when modality filtering is disabled, so this is
 * a safe drop-in for useModalities.
 *
 * @returns {Object} { modalityOptions, loading, error, refetch }
 */
export const useAllowedModalities = () => {
  const [modalityOptions, setModalityOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchModalities = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeGetCall('/my-modalities');

      if (response?.data?.success) {
        setModalityOptions(response.data.data || []);
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch modalities');
      }
    } catch (err) {
      console.error('Error fetching allowed modalities:', err);
      setError(err.message || 'Failed to fetch modalities');
      // Fallback to a sensible default if the API fails.
      setModalityOptions([
        { label: 'CT', value: 'CT' },
        { label: 'US', value: 'US' },
        { label: 'MR', value: 'MR' },
        { label: 'DX', value: 'DX' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModalities();
  }, []);

  const refetch = () => {
    fetchModalities();
  };

  return {
    modalityOptions,
    loading,
    error,
    refetch
  };
};
